import {
  formatPrice,
  formatReservationDate,
  useEstimateSubscriptionProcessingFeeQuery,
} from "@stepapp/core";
import { BottomSheet, Button, Typography, spacing } from "@stepapp/ui";
import { useRouter } from "expo-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet } from "react-native";
import { AppStorage } from "../../services/AppStorage";

type ModifyReservationProps = {
  reservationId: string;
  subscriptionId?: string;
  startDate: string;
  isVisible: boolean;
  oneTimeService: boolean;
  cancellationFee: string;
  toggle: () => void;
  handleQuestionEdit: (v: string) => void;
};

const CancelReservationSheet = ({
  reservationId,
  subscriptionId = "",
  startDate,
  oneTimeService,
  isVisible,
  cancellationFee = "",
  toggle,
  handleQuestionEdit,
}: ModifyReservationProps) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const { data: subscruptionProcessingFee } = useEstimateSubscriptionProcessingFeeQuery({
    variables: { subscriptionId },
    skip: !subscriptionId,
  });

  const subscriptionCancellationFee = subscruptionProcessingFee?.estimateSubscriptionProcessingFee
    ?.amount
    ? priceFormatter(subscruptionProcessingFee?.estimateSubscriptionProcessingFee?.amount, {
        currency: subscruptionProcessingFee?.estimateSubscriptionProcessingFee?.currency,
      })
    : "";

  const handleClose = useCallback(() => {
    toggle();
  }, [toggle]);

  const handleChangeDate = useCallback(() => {
    toggle();
    setTimeout(() => handleQuestionEdit("date"), 500);
  }, [handleQuestionEdit, toggle]);

  const handleCancelReservation = useCallback(
    (cancelSub?: boolean) => {
      const params = cancelSub ? { subscriptionId } : {};

      toggle();
      setTimeout(
        () =>
          navigate({
            pathname: `/calendar/${reservationId}/cancel-reservation`,
            params,
          }),
        500,
      );
    },
    [toggle, navigate, reservationId, subscriptionId],
  );

  return (
    <BottomSheet
      contentStyle={styles.container}
      enableScroll={false}
      toggle={handleClose}
      visible={isVisible}
    >
      <Typography variant="newH2">{t("screens.cancelReservation.serviceCancellation")}</Typography>
      <Typography variant="paragraph" type="secondary" style={styles.cancelReservationDescription}>
        {oneTimeService
          ? t("screens.cancelReservation.cancelOrEditReservation")
          : t("screens.cancelReservation.cancelSingleReservationInSubscription", {
              date: formatReservationDate(startDate, "dd MMMM"),
            })}
      </Typography>
      {!!cancellationFee && (
        <Typography variant="bodyBold" decoration="underline">
          {t("screens.serviceDetails.showCancellationRules")}
        </Typography>
      )}

      <Button
        label={t("screens.cancelReservation.cancelAppointment", {
          fee: cancellationFee && `(${cancellationFee})`,
        })}
        onPress={() => handleCancelReservation()}
        type="primary"
        style={styles.firstButton}
      />
      <Button
        label={t("screens.serviceDetails.changeDate", {
          fee: cancellationFee && `(${cancellationFee})`,
        })}
        onPress={handleChangeDate}
        type="secondary"
        style={styles.button}
      />
      {!oneTimeService && (
        <Button
          label={t("screens.cancelReservation.cancelEntireSubscription", {
            fee: subscriptionCancellationFee && `(${subscriptionCancellationFee})`,
          })}
          onPress={() => handleCancelReservation(true)}
          type="secondary"
          style={styles.button}
        />
      )}
    </BottomSheet>
  );
};

export default CancelReservationSheet;

const styles = StyleSheet.create({
  button: {
    ...Platform.select({
      default: {},
      web: { marginTop: spacing.unit * 1.5 },
    }),
  },
  container: {
    padding: spacing.unit * 2.5,
  },
  cancelReservationDescription: {
    marginTop: spacing.unit,
    marginBottom: spacing.unit,
  },
  firstButton: {
    marginTop: spacing.unit * 3,
  },
});
